/* Sofia Lark ambient light: the shadow of palms and leafy branches by the shop window, as if the
   afternoon sun threw them across the wall. Drawn once into canvases and used as the backgrounds
   of fixed layers behind the page (so black sections, photographs and cards cover them). The
   plants are one tall tile that repeats downwards without a seam; while the page scrolls the tile
   drifts up at a fraction of the speed, like a wall further away than the content, and CSS sways
   it very slowly. A fixed seed means every visit sees the same plants.
   The drawing is made in 960-unit coordinates and rendered at the screen's own pixel density
   (within a memory budget), so it stays smooth on phones and Retina screens. */
(function () {
  'use strict';

  var W = 960;
  var H = 600;
  var TILE = 1500;      // height of the repeating plant tile, in canvas pixels
  var RATE = 0.12;      // how far the shadow travels per pixel of scroll
  var INK = '24, 21, 16';
  var PHONE = window.matchMedia('(max-width: 749px)').matches;
  // pixels per drawing unit: enough for the screen's density, capped so the three working canvases
  // stay within what phones allow
  var S = (function () {
    var cssWidth = Math.max(window.innerWidth * 1.12, 1100);
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    var budget = PHONE ? 4.5e6 : 7e6;
    var cap = Math.sqrt(budget / (W * TILE));
    return Math.max(1, Math.min(cssWidth * dpr / W, cap));
  })();
  // phones get a softer shadow: the plants read as light, not as drawn shapes
  var SOFT = PHONE ? 1.25 : 1;

  function rng(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // A canvas measured in drawing units; its context draws in those units.
  function canvas(height) {
    var c = document.createElement('canvas');
    c.width = Math.round(W * S);
    c.height = Math.round((height || H) * S);
    c.getContext('2d').setTransform(S, 0, 0, S, 0, 0);
    return c;
  }

  // Draw a blurred copy of source onto ctx (same size, device pixels). Where the canvas has no
  // blur filter (Safari before 18), the shadow of the image is used instead: it is a true
  // gaussian blur in the shadow colour, which is the ink colour anyway. The image itself is drawn
  // off the canvas, so only its shadow lands.
  function blurInto(ctx, source, px, alpha) {
    var r = px * S * SOFT;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = alpha;
    if ('filter' in ctx) {
      ctx.filter = 'blur(' + r.toFixed(2) + 'px)';
      ctx.drawImage(source, 0, 0);
    } else {
      var off = source.width + 100;
      ctx.shadowColor = 'rgb(' + INK + ')';
      ctx.shadowBlur = r * 2;
      ctx.shadowOffsetX = off;
      ctx.drawImage(source, -off, 0);
    }
    ctx.restore();
  }

  function free(c) {
    c.width = 0;
    c.height = 0;
  }

  // A curve sampled into points: start, heading, length, bend per step and a pull downwards.
  function stem(random, x, y, angle, length, bend, droop, steps) {
    var points = [];
    for (var i = 0; i <= steps; i++) {
      points.push([x, y, angle]);
      angle += bend + (random() - 0.5) * 0.02;
      // gravity turns the heading towards straight down (PI / 2)
      angle += Math.sin(Math.PI / 2 - angle) * droop;
      x += Math.cos(angle) * length / steps;
      y += Math.sin(angle) * length / steps;
    }
    return points;
  }

  function strokeStem(ctx, points, from, to) {
    for (var i = 1; i < points.length; i++) {
      ctx.beginPath();
      ctx.moveTo(points[i - 1][0], points[i - 1][1]);
      ctx.lineTo(points[i][0], points[i][1]);
      ctx.lineWidth = from + (to - from) * (i / points.length);
      ctx.stroke();
    }
  }

  // A long, narrow, slightly drooping blade: base on the stem, pointed tip.
  function blade(ctx, x, y, angle, length, width, sag) {
    var tx = x + Math.cos(angle) * length;
    var ty = y + Math.sin(angle) * length + sag;
    var mx = x + Math.cos(angle) * length * 0.5;
    var my = y + Math.sin(angle) * length * 0.5 + sag * 0.15;
    var nx = -Math.sin(angle) * width;
    var ny = Math.cos(angle) * width;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(mx + nx, my + ny, tx, ty);
    ctx.quadraticCurveTo(mx - nx, my - ny, x, y);
    ctx.fill();
  }

  // An ovate leaf on a short stalk: rounded at the base, pointed at the tip.
  function leaf(ctx, x, y, angle, length, width) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(length * 0.16, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(length * 0.14, 0);
    ctx.bezierCurveTo(length * 0.2, -width, length * 0.72, -width * 0.9, length, 0);
    ctx.bezierCurveTo(length * 0.72, width * 0.9, length * 0.2, width, length * 0.14, 0);
    ctx.fill();
    ctx.restore();
  }

  // A palm frond: an arching midrib with paired leaflets that shorten towards the tip.
  function frond(ctx, random, x, y, angle, length, bend, reach) {
    var pts = stem(random, x, y, angle, length, bend, 0.012, 90);
    strokeStem(ctx, pts, 5, 1.5);
    for (var i = 6; i < pts.length - 2; i += 3) {
      var p = pts[i];
      var t = i / pts.length;
      var len = reach * (1 - t * 0.72) * (0.86 + random() * 0.2);
      var spread = 0.95 - t * 0.35;
      [-1, 1].forEach(function (side) {
        var a = p[2] + side * (spread + (random() - 0.5) * 0.16);
        blade(ctx, p[0], p[1], a, len, 3.2 + len * 0.045, len * 0.22);
      });
    }
  }

  // A twig with alternate leaves, like a fig or olive branch.
  function branch(ctx, random, x, y, angle, length, count, size) {
    var pts = stem(random, x, y, angle, length, 0.004, 0.006, 70);
    strokeStem(ctx, pts, 3.5, 1.2);
    for (var k = 0; k < count; k++) {
      var p = pts[Math.floor((pts.length - 1) * (0.1 + 0.88 * k / count))];
      var side = k % 2 ? 1 : -1;
      var a = p[2] + side * (0.55 + random() * 0.45);
      var len = size * (0.8 + random() * 0.35);
      leaf(ctx, p[0], p[1], a, len, len * (0.3 + random() * 0.06));
    }
    var tip = pts[pts.length - 1];
    leaf(ctx, tip[0], tip[1], tip[2], size * 0.85, size * 0.27);
  }

  // Everything on the plant tile, near (sharper) and far (softer) from the wall.
  function plants(n, f) {
    var random = rng(11);

    // top of the tile: a frond and a branch further back, the frond that reads first and a fig
    // branch above it
    frond(f, random, W + 30, 300, 200 * Math.PI / 180, 470, -0.0025, 110);
    branch(f, random, W + 10, 500, 196 * Math.PI / 180, 280, 10, 44);
    frond(n, random, W + 40, -40, 152 * Math.PI / 180, 640, 0.0035, 150);
    branch(n, random, W - 330, -40, 96 * Math.PI / 180, 230, 9, 52);

    // further down: a long branch reaching in and a lower, softer frond
    branch(n, random, W + 20, 930, 197 * Math.PI / 180, 380, 13, 50);
    frond(f, random, W + 30, 1080, 168 * Math.PI / 180, 520, 0.002, 125);
    branch(f, random, W - 120, 1330, 214 * Math.PI / 180, 220, 8, 40);
  }

  function drawPlants() {
    var near = canvas(TILE);
    var far = canvas(TILE);
    var n = near.getContext('2d');
    var f = far.getContext('2d');
    [n, f].forEach(function (ctx) {
      ctx.fillStyle = 'rgb(' + INK + ')';
      ctx.strokeStyle = 'rgb(' + INK + ')';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    });

    // Drawn three times, a tile above and below, so whatever crosses an edge comes back in on the
    // other side and the repeat has no seam.
    [-TILE, 0, TILE].forEach(function (dy) {
      n.save(); f.save();
      n.translate(0, dy); f.translate(0, dy);
      plants(n, f);
      n.restore(); f.restore();
    });

    var out = canvas(TILE);
    var o = out.getContext('2d');
    blurInto(o, far, 5, 0.5);
    blurInto(o, near, 3.5, 0.95);
    free(far);
    free(near);

    // The plants live along the right side and fade out before the middle of the page.
    o.globalAlpha = 1;
    o.globalCompositeOperation = 'destination-in';
    var g = o.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0.12, 'rgba(0,0,0,0)');
    g.addColorStop(0.5, 'rgba(0,0,0,1)');
    o.fillStyle = g;
    o.fillRect(0, 0, W, TILE);
    return out;
  }

  function drawWindow() {
    var c = canvas();
    var ctx = c.getContext('2d');
    var skew = 0.5;
    ctx.fillStyle = 'rgba(' + INK + ',0.6)';
    [[60, 26], [330, 16]].forEach(function (b) {
      ctx.beginPath();
      ctx.moveTo(b[0], -10);
      ctx.lineTo(b[0] + b[1], -10);
      ctx.lineTo(b[0] + b[1] + H * skew, H + 10);
      ctx.lineTo(b[0] + H * skew, H + 10);
      ctx.closePath();
      ctx.fill();
    });
    ctx.beginPath();
    ctx.moveTo(-10, 300);
    ctx.lineTo(W + 10, 225);
    ctx.lineTo(W + 10, 248);
    ctx.lineTo(-10, 324);
    ctx.closePath();
    ctx.fill();
    var out = canvas();
    blurInto(out.getContext('2d'), c, 14, 1);
    free(c);
    return out;
  }

  // Use a canvas as an element's background. A blob keeps the large image out of a data: string.
  function paint(el, c, done) {
    function set(u) {
      el.style.backgroundImage = 'url(' + u + ')';
      free(c);
      done();
    }
    if (c.toBlob && window.URL && URL.createObjectURL) {
      c.toBlob(function (blob) {
        if (blob) set(URL.createObjectURL(blob));
        else set(c.toDataURL('image/png'));
      }, 'image/png');
    } else {
      set(c.toDataURL('image/png'));
    }
  }

  function parallax(root, drift) {
    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    var tile = 0;
    var queued = false;

    function place() {
      queued = false;
      var y = still.matches ? 0 : (window.scrollY * RATE) % tile;
      drift.style.transform = 'translate3d(0, ' + (-y).toFixed(1) + 'px, 0)';
    }
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(place);
    }
    // The tile is sized to the window: at least as wide as the layer, never so narrow on a phone
    // that the leaves turn into twigs.
    function size() {
      var width = Math.max(root.clientWidth * 1.12, 1100);
      tile = width * TILE / W;
      root.style.setProperty('--sl-tile-w', width.toFixed(0) + 'px');
      root.style.setProperty('--sl-tile', tile.toFixed(0) + 'px');
      place();
    }

    size();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', size);
    if (still.addEventListener) still.addEventListener('change', place);
  }

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    var leaves = root.querySelector('[data-sl-ambient-leaves]');
    var frame = root.querySelector('[data-sl-ambient-window]');
    var drift = root.querySelector('[data-sl-ambient-drift]');
    try {
      var pending = (frame ? 1 : 0) + (leaves ? 1 : 0);
      var done = function () {
        if (--pending <= 0) root.classList.add('is-ready');
      };
      if (frame) paint(frame, drawWindow(), done);
      if (leaves) paint(leaves, drawPlants(), done);
      if (drift) parallax(root, drift);
      if (!pending) root.classList.add('is-ready');
    } catch (err) {
      root.hidden = true;
    }
  }

  function boot() {
    document.querySelectorAll('[data-sl-ambient]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
